import type { NuxtLocale } from '~/constants/locales'
import type {
  Configuration,
  SurfaceMaps,
  SurfaceOptionsGroup,
  SurfaceTypesMapValue,
  AccessoriesChangeBody,
} from '~/types/configurator'

export default function useConfigurationAccessoryChange(
  configuration: Ref<Configuration | null>,
  values: Ref<Record<string, Configuration['values'][number]>>,
  multiselectableAccessories: Ref<Record<string, Configuration['values']>>,
  currentValues: ComputedRef<Configuration['values']>,
  surfaceTypes: ComputedRef<Map<number, SurfaceTypesMapValue>>,
  surfaceMaps: ComputedRef<SurfaceMaps>,
  surfaceOptionsGroups: ComputedRef<Map<string, SurfaceOptionsGroup>>,
  updateNonRemovableAccessories: () => void
) {
  const configuratorRepo = createConfiguratorRepository()
  const { locale } = useI18n()

  function getOldAccessory(
    accessoriesOption: Configuration['accessoriesOptions'][number]
  ) {
    // does not return multiselectable accessories - works only for single select
    const partCategoryNid = accessoriesOption.partCategoryNid
    const value = values.value[partCategoryNid]
    const partAccessoryNid = value?.partNid
    const oldAccessory = configuration.value?.accessoriesOptions
      .find((accessory) => accessory.partCategoryNid === partCategoryNid)
      ?.values.find((accessory) => accessory.accessoryNid === partAccessoryNid)
    return oldAccessory
  }

  function isPicked(
    accessoriesOption: Configuration['accessoriesOptions'][number],
    accessory: Configuration['accessoriesOptions'][number]['values'][number]
  ) {
    return configuratorUtils.isAccessoryPicked(
      accessoriesOption,
      accessory,
      values.value,
      multiselectableAccessories.value
    )
  }

  function buildChangeVector(
    accessoriesOption: Configuration['accessoriesOptions'][number],
    oldAccessory:
      | Configuration['accessoriesOptions'][number]['values'][number]
      | undefined,
    newAccessory: Configuration['accessoriesOptions'][number]['values'][number]
  ): AccessoriesChangeBody['changeVector'] {
    if (
      accessoriesOption.multiselectable &&
      newAccessory.accessoryTranslationOfNid
    ) {
      // multiselectable - based on being picked send removal or addition of accessory
      return [
        {
          partCategoryTranslationOfNid:
            accessoriesOption.partCategoryTranslationOfNid,
          partTranslationOfNid: newAccessory.accessoryTranslationOfNid,
          removing: isPicked(accessoriesOption, newAccessory),
        },
      ]
    } else if (newAccessory.accessoryTranslationOfNid) {
      return [
        {
          partCategoryTranslationOfNid:
            accessoriesOption.partCategoryTranslationOfNid,
          partTranslationOfNid: newAccessory.accessoryTranslationOfNid,
          removing: false,
        },
      ]
    } else if (
      !newAccessory.accessoryTranslationOfNid &&
      oldAccessory?.accessoryTranslationOfNid
    ) {
      // non vnp - selected empty accessory - send removal of old accessory
      return [
        {
          partCategoryTranslationOfNid:
            accessoriesOption.partCategoryTranslationOfNid,
          partTranslationOfNid: oldAccessory.accessoryTranslationOfNid,
          removing: true,
        },
      ]
    } else {
      // fallback - send empty change vector
      console.error('Should not happen: Unable to build change vector')
      return []
    }
  }

  async function checkAccessoryChange(
    accessoriesOption: Configuration['accessoriesOptions'][number],
    newAccessory: Configuration['accessoriesOptions'][number]['values'][number]
  ) {
    if (!configuration.value) {
      return []
    }

    const oldAccessory = getOldAccessory(accessoriesOption)
    const changeVector = buildChangeVector(
      accessoriesOption,
      oldAccessory,
      newAccessory
    )

    if (
      (oldAccessory && oldAccessory.changeNeedsApiCall) ||
      newAccessory.changeNeedsApiCall
    ) {
      try {
        const response = await configuratorRepo.getAccessoryChange(
          {
            values: currentValues.value,
            changeVector: changeVector,
          },
          {
            language: convertNuxtLocale(locale.value as NuxtLocale),
            productNid: configuration.value?.productNid,
          }
        )
        // return all accessory changes
        return response.categories
      } catch (error) {
        console.error(error)
        return undefined
      }
    } else if (!accessoriesOption.multiselectable) {
      // normal accessory change - no api call needed
      // return single accessory change
      return [
        {
          accessoryCategoryTranslationOfNid:
            accessoriesOption.partCategoryTranslationOfNid,
          accessoriesTranslationsOfNid: [
            newAccessory.accessoryTranslationOfNid,
          ],
        },
      ]
    } else {
      // multiselectable accessory change - no api call needed
      // return full changes - all accessories in the category with needed change
      const accessories =
        multiselectableAccessories.value[
          accessoriesOption.partCategoryNid
        ]?.map((accessory) => accessory.partTranslationOfNid) || []
      if (changeVector.at(0)?.removing) {
        // remove the accessory from the list
        const index = accessories?.findIndex(
          (accessory) => accessory === newAccessory.accessoryTranslationOfNid
        )
        if (index !== undefined && index !== -1) {
          accessories?.splice(index, 1)
        }
      } else {
        // add the accessory to the list
        if (!accessories?.includes(newAccessory.accessoryTranslationOfNid)) {
          accessories?.push(newAccessory.accessoryTranslationOfNid)
        }
      }

      return [
        {
          accessoryCategoryTranslationOfNid:
            accessoriesOption.partCategoryTranslationOfNid,
          accessoriesTranslationsOfNid: accessories,
        },
      ]
    }
  }

  function convertChangesIntoValues(
    changes: {
      accessoryCategoryTranslationOfNid: number
      accessoriesTranslationsOfNid: (number | null | undefined)[]
    }[]
  ) {
    return changes
      .map((change) => {
        const accessoriesOption = configuration.value?.accessoriesOptions.find(
          (option) =>
            option.partCategoryTranslationOfNid ===
            change.accessoryCategoryTranslationOfNid
        )
        // could not find accessory category - skip (should not happen)
        if (!accessoriesOption) return

        return {
          accessoriesOption,
          values: change.accessoriesTranslationsOfNid
            .map((accessoryTranslationOfNid) => {
              const accessory = accessoriesOption.values.find(
                (option) =>
                  option.accessoryTranslationOfNid === accessoryTranslationOfNid
              )
              // could not find accessory - skip (should not happen)
              if (!accessory) return

              const { surfaceOption, accessoryValue } =
                configuratorUtils.convertAccessoryToValue(
                  accessory,
                  accessoriesOption,
                  currentValues.value,
                  surfaceTypes.value,
                  surfaceMaps.value,
                  surfaceOptionsGroups.value
                )
              return {
                surfaceOption,
                accessory,
                accessoryValue,
              }
            })
            .filter((accessory) => accessory !== undefined),
        }
      })
      .filter((change) => change !== undefined)
  }

  function handleSurfaceOptions(
    accessoriesOption: Configuration['accessoriesOptions'][number],
    accessory: Configuration['accessoriesOptions'][number]['values'][number],
    surfaceOption?: Configuration['surfacesOptions'][number]
  ) {
    const index = configuration.value?.surfacesOptions.findIndex((value) =>
      !accessoriesOption.multiselectable
        ? value.partCategoryNid === accessoriesOption.partCategoryNid
        : value.partCategoryNid === accessoriesOption.partCategoryNid &&
          value.accessoryNid === accessory.accessoryNid
    )

    if (
      index !== undefined &&
      index !== -1 &&
      (!accessoriesOption.multiselectable ||
        (accessoriesOption.multiselectable &&
          isPicked(accessoriesOption, accessory)))
    ) {
      configuration.value?.surfacesOptions.splice(index, 1)
      updateNonRemovableAccessories()
    }

    if (
      surfaceOption &&
      (!accessoriesOption.multiselectable ||
        (accessoriesOption.multiselectable &&
          !isPicked(accessoriesOption, accessory)))
    ) {
      configuration.value?.surfacesOptions.push(surfaceOption)
      updateNonRemovableAccessories()
    }
  }

  function applyAccessoryChanges(
    changeOrigin: Configuration['accessoriesOptions'][number]['values'][number],
    changes: {
      accessoryCategoryTranslationOfNid: number
      accessoriesTranslationsOfNid: (number | null | undefined)[]
    }[]
  ) {
    const addedAccessories: {
      accessoriesOption: Configuration['accessoriesOptions'][number]
      accessory: Configuration['accessoriesOptions'][number]['values'][number]
    }[] = []
    const removedAccessories: {
      accessoriesOption: Configuration['accessoriesOptions'][number]
      accessory: Configuration['accessoriesOptions'][number]['values'][number]
    }[] = []
    const swappedAccessories: {
      accessoriesOption: Configuration['accessoriesOptions'][number]
      oldAccessory: Configuration['accessoriesOptions'][number]['values'][number]
      newAccessory: Configuration['accessoriesOptions'][number]['values'][number]
    }[] = []

    const convertedChanges = convertChangesIntoValues(changes)

    convertedChanges.forEach((change) => {
      const accessoriesOption = change.accessoriesOption
      if (!accessoriesOption.multiselectable) {
        const oldAccessory = getOldAccessory(accessoriesOption)
        change.values.forEach((change) => {
          const { surfaceOption, accessory, accessoryValue } = change
          if (
            oldAccessory &&
            oldAccessory.accessoryNid !== accessory.accessoryNid
          ) {
            // change happened
            // empty option to non empty option or vice versa (non vnp)
            // non empty option to different non empty option
            if (
              oldAccessory.accessoryTranslationOfNid !==
                changeOrigin.accessoryTranslationOfNid &&
              accessory.accessoryTranslationOfNid !==
                changeOrigin.accessoryTranslationOfNid
            ) {
              if (
                oldAccessory.accessoryNid &&
                accessory.accessoryNid &&
                oldAccessory.accessoryNid !== accessory.accessoryNid
              ) {
                swappedAccessories.push({
                  accessoriesOption,
                  oldAccessory,
                  newAccessory: accessory,
                })
              } else if (!oldAccessory.accessoryNid && accessory.accessoryNid) {
                // empty option to non empty option
                addedAccessories.push({
                  accessoriesOption,
                  accessory,
                })
              } else if (oldAccessory.accessoryNid && !accessory.accessoryNid) {
                // non empty option to empty option
                removedAccessories.push({
                  accessoriesOption,
                  accessory: oldAccessory,
                })
              }
            }
            values.value[accessoriesOption.partCategoryNid] = accessoryValue
            handleSurfaceOptions(accessoriesOption, accessory, surfaceOption)
          }
        })
      } else {
        const newMultiselectableAccessories: Configuration['values'] = []
        const oldMultiselectableAccessories =
          multiselectableAccessories.value[accessoriesOption.partCategoryNid] ||
          []
        // 1. iterate over changes - check if in old multiselectable accessories (isPicked) - if not - it's added
        change.values.forEach((change) => {
          const { surfaceOption, accessory, accessoryValue } = change
          newMultiselectableAccessories.push(accessoryValue)
          const isPresent = isPicked(accessoriesOption, accessory)
          if (!isPresent) {
            // change happened (accessory added)
            if (
              accessory.accessoryTranslationOfNid !==
              changeOrigin.accessoryTranslationOfNid
            ) {
              addedAccessories.push({
                accessoriesOption,
                accessory,
              })
            }
            handleSurfaceOptions(accessoriesOption, accessory, surfaceOption)
          }
        })

        // 2. iterate over old multiselectable accessories - check if in new multiselectable accessories - if not it's removed
        oldMultiselectableAccessories.forEach((oldAccessoryValue) => {
          const oldAccessory = configuration.value?.accessoriesOptions
            .find(
              (option) =>
                option.partCategoryNid === accessoriesOption.partCategoryNid
            )
            ?.values.find(
              (option) => option.accessoryNid === oldAccessoryValue.partNid
            )
          const isPresent = newMultiselectableAccessories.find(
            (newAccessory) => newAccessory.partNid === oldAccessoryValue.partNid
          )
          if (!isPresent && oldAccessory) {
            // change happened (accessory removed)
            if (
              oldAccessory.accessoryTranslationOfNid !==
              changeOrigin.accessoryTranslationOfNid
            ) {
              removedAccessories.push({
                accessoriesOption,
                accessory: oldAccessory,
              })
            }
            handleSurfaceOptions(accessoriesOption, oldAccessory)
          }
        })

        // 3. set new multiselectable accessories
        multiselectableAccessories.value[accessoriesOption.partCategoryNid] =
          newMultiselectableAccessories
      }
    })

    return {
      addedAccessories: addedAccessories,
      removedAccessories: removedAccessories,
      swappedAccessories: swappedAccessories,
    }
  }

  return {
    checkAccessoryChange,
    applyAccessoryChanges,
  }
}
